import { memo, useCallback } from 'react';

import {
  BulkEditPlainTextCustomFieldModal,
  type BulkEditPlainTextCustomFieldModalProps,
} from '~/components/Modals/BulkEditCustomFieldModal/BulkEditPlainTextCustomFieldModal';
import { useSelectAllCustomFieldModal } from '~/hooks/customFields/useSelectAllCustomFieldModal';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdateItemsCustomFieldValue } from '~/utils/CustomFields/useUpdateItemsCustomFieldValue';

export type PrivateBulkEditPlainTextCustomFieldModalProps = Omit<BulkEditPlainTextCustomFieldModalProps, 'onSave'>;

const resolveInitialValue: Parameters<typeof useSelectAllCustomFieldModal>[0]['resolveInitialValue'] = (
  common,
  field,
) => {
  const plainTextValue = common.value && 'plainTextValue' in common.value ? common.value.plainTextValue : null;

  return {
    ...field,
    value: null,
    values: null,
    plainTextValue: plainTextValue ?? undefined,
    dateValue: undefined,
  };
};

export const PrivateBulkEditPlainTextCustomFieldModal = memo(
  (props: AirModalProps<PrivateBulkEditPlainTextCustomFieldModalProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceId = currentWorkspace?.id;
    const { updateItemsCustomFieldValue } = useUpdateItemsCustomFieldValue();

    const { selectAll, initialValues, initialStatus, isLoadingInitialValues, handleSelectAllSave } =
      useSelectAllCustomFieldModal({
        field: props.field,
        fallbackInitialValues: props.initialValues,
        resolveInitialValue,
      });

    const onSave = useCallback<BulkEditPlainTextCustomFieldModalProps['onSave']>(
      async (params) => {
        if (selectAll) {
          await handleSelectAllSave({
            baseCustomField: params.baseCustomField,
            operation: 'plainText',
            trackLocation: 'context-menu-bulk-edit',
            plainTextValue: params.nextPlainTextValue,
          });
          return;
        }

        await updateItemsCustomFieldValue({
          ...params,
          trackingLocation: 'context-menu-bulk-edit',
          workspaceId,
        });
      },
      [handleSelectAllSave, selectAll, updateItemsCustomFieldValue, workspaceId],
    );

    return (
      <BulkEditPlainTextCustomFieldModal
        {...props}
        initialValues={initialValues}
        initialStatus={initialStatus}
        isLoadingInitialValues={isLoadingInitialValues}
        onSave={onSave}
      />
    );
  },
);
PrivateBulkEditPlainTextCustomFieldModal.displayName = 'PrivateBulkEditPlainTextCustomFieldModal';
